import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { type CellClassParams, type CellStyle, type CellStyleFunc, type ValueFormatterParams } from 'ag-grid-community';
import moment from 'moment/moment';

import { currentCompanyDateFormatSelector } from 'domain/companies';
import { SYMBOL_FOR_EMPTY_CELL } from 'components/AgGrid/helpers';

const useAgGridColumnTypes = () => {
  const companyDateFormat = useSelector(currentCompanyDateFormatSelector);
  const { formatMessage } = useIntl();
  const dateFormat = companyDateFormat || 'DD/MM/YYYY';

  return useMemo(
    () =>
      ({
        string: {
          cellStyle: ((params: CellClassParams): CellStyle => {
            if (['address', 'email'].includes(params.colDef?.field ?? '')) {
              return { textAlign: 'left' };
            }
            return {};
          }) as CellStyleFunc,
        },
        integer: {
          cellStyle: { textAlign: 'right' } as CellStyle,
        },
        date: {
          cellStyle: { unicodeBidi: 'plaintext' } as CellStyle,
          filterParams: {
            format: dateFormat,
            closeOnApply: true,
            // to compare dates in local timezone when filtering
            filterValueTransform: (value: string): string => `${value}Z`,
          },
          valueFormatter: (params: ValueFormatterParams): string =>
            params.value ? moment(`${params.value}Z`).format(`${dateFormat} HH:mm:ss`) : SYMBOL_FOR_EMPTY_CELL,
        },
        boolean: {
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          } as CellStyle,
          filterParams: {
            valueFormatter: (params: ValueFormatterParams): string =>
              formatMessage({
                id: `button.${params.value ? 'yes' : 'no'}`,
                defaultMessage: params.value ? 'Yes' : 'No',
              }),
          },
        },
        select: {},
      }) as const,
    [dateFormat, formatMessage],
  );
};

export default useAgGridColumnTypes;
